// Contrato único de la API.
// El frontend SIEMPRE importa de aquí → contexto total del backend.
// Añade aquí cada ruta, DTO y schema. Nada de duplicar tipos a mano.

import { z } from "zod";

// ── Respuesta estándar ──────────────────────────────────────────
export type ApiResponse<T> = {
  ok: true;
  data: T;
};

export type ApiError = {
  ok: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
};

// ── Health ──────────────────────────────────────────────────────
export const HealthSchema = z.object({
  status: z.literal("ok"),
  uptime: z.number(),
  version: z.string(),
  memoryMb: z.number(),
});
export type Health = z.infer<typeof HealthSchema>;

// ── WhatsApp (Baileys) ──────────────────────────────────────────
export const WhatsappConnectionSchema = z.enum([
  "idle",
  "connecting",
  "open",
  "close",
  "logged_out",
]);
export type WhatsappConnection = z.infer<typeof WhatsappConnectionSchema>;

// Datos de la cuenta vinculada (quién inició sesión).
export const WhatsappProfileSchema = z.object({
  /** Número/JID principal, p. ej. 54911...@s.whatsapp.net */
  id: z.string(),
  /** Nombre público (pushName) de la cuenta. */
  name: z.string().nullable(),
  /** Número de teléfono en formato PN. */
  phoneNumber: z.string().nullable(),
  /** Identificador LID (nuevo formato de WhatsApp). */
  lid: z.string().nullable(),
  /** URL de la foto de perfil (puede expirar; el backend la refresca). */
  profilePicUrl: z.string().nullable(),
  /** Plataforma del dispositivo vinculado (android/ios/web/smba…). */
  platform: z.string().nullable(),
  /** Número de dispositivo vinculado (el :N de me.id). */
  deviceId: z.number().int().nullable(),
  /** true cuando la sesión ya está registrada (vinculada). */
  registered: z.boolean(),
});
export type WhatsappProfile = z.infer<typeof WhatsappProfileSchema>;

export const WhatsappStatusSchema = z.object({
  connection: WhatsappConnectionSchema,
  qr: z.string().nullable(),
  profile: WhatsappProfileSchema.nullable(),
  messageCount: z.number().int().nonnegative(),
  startedAt: z.string().nullable(),
  connectedAt: z.string().nullable(),
  lastDisconnectReason: z.string().nullable(),
  updatedAt: z.string(),
});
export type WhatsappStatus = z.infer<typeof WhatsappStatusSchema>;

export const WhatsappLogSchema = z.object({
  id: z.string(),
  at: z.string(),
  level: z.enum(["info", "warn", "error"]),
  event: z.string(),
  detail: z.string().nullable(),
});
export type WhatsappLogEntry = z.infer<typeof WhatsappLogSchema>;
export const WhatsappLogsSchema = z.array(WhatsappLogSchema);
export type WhatsappLogs = z.infer<typeof WhatsappLogsSchema>;

// ── Sesiones (multi-dispositivo) ────────────────────────────────
export const WhatsappSessionMetaSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Token propio de la sesión (para automatizar solo esta cuenta). */
  token: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Webhook propio de la sesión (null = usa el global). */
  webhookUrl: z.string().nullable(),
  /** Eventos filtrados para este webhook (vacío = usa el filtro global). */
  webhookEvents: z.array(z.string()),
  /** true si la sesión tiene un secreto HMAC propio (no se expone el valor). */
  webhookSecretSet: z.boolean(),
});
export type WhatsappSessionMeta = z.infer<typeof WhatsappSessionMetaSchema>;

export const WhatsappSessionSchema = WhatsappSessionMetaSchema.extend({
  status: WhatsappStatusSchema,
});
export type WhatsappSession = z.infer<typeof WhatsappSessionSchema>;

export const WhatsappSessionsSchema = z.array(WhatsappSessionSchema);
export type WhatsappSessions = z.infer<typeof WhatsappSessionsSchema>;

export const CreateWhatsappSessionSchema = z.object({
  label: z.string().min(1).max(60),
});
export type CreateWhatsappSessionInput = z.infer<typeof CreateWhatsappSessionSchema>;

export const RenameWhatsappSessionSchema = CreateWhatsappSessionSchema;
export type RenameWhatsappSessionInput = z.infer<typeof RenameWhatsappSessionSchema>;

// Webhook por sesión. `url: null` limpia la config (vuelve al webhook global).
// `secret`/`events` ausentes = se conservan; `secret: null` lo borra.
const HttpUrlSchema = z
  .string()
  .url()
  .max(2048)
  .refine((value) => /^https?:\/\//i.test(value), "La URL debe ser http(s)");

export const SetSessionWebhookSchema = z.object({
  url: HttpUrlSchema.nullable(),
  secret: z.string().max(200).nullable().optional(),
  events: z.array(z.string().min(1).max(40)).max(20).optional(),
});
export type SetSessionWebhookInput = z.infer<typeof SetSessionWebhookSchema>;

export type WhatsappMessageEvent = {
  direction: "in" | "out";
  jid: string;
  text: string | null;
  messageType: string | null;
  hasMedia: boolean;
  at: string;
};

// Resultado de una acción encolada (done/failed).
export type WhatsappActionEvent = {
  actionId: string;
  actionType: string;
  status: OutboxStatus;
  messageId: string | null;
  error: string | null;
};

// Eventos que el backend empuja por WebSocket al panel React.
export type WhatsappServerEvent =
  | { type: "sessions"; data: WhatsappSessions }
  | { type: "status"; sessionId: string; data: WhatsappStatus }
  | { type: "log"; sessionId: string; data: WhatsappLogEntry }
  | { type: "message"; sessionId: string; data: WhatsappMessageEvent }
  | { type: "action"; sessionId: string; data: WhatsappActionEvent };

// ── Auth (credenciales estáticas) ───────────────────────────────
export const LoginSchema = z.object({
  username: z.string().min(1).max(80),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof LoginSchema>;

export const LoginResultSchema = z.object({
  token: z.string(),
  username: z.string(),
});
export type LoginResult = z.infer<typeof LoginResultSchema>;

// ── Acciones de WhatsApp ────────────────────────────────────────
const JidSchema = z.string().min(5).max(80);
const QuotedSchema = z.string().min(1).max(160).optional();

export const SendTextActionSchema = z.object({
  type: z.literal("text"),
  to: JidSchema,
  text: z.string().min(1).max(65536),
  quotedId: QuotedSchema,
});

export const SendMediaActionSchema = z.object({
  type: z.literal("media"),
  to: JidSchema,
  mediaType: z.enum(["image", "video", "audio", "document"]),
  url: z.string().url().optional(),
  base64: z.string().optional(),
  mimetype: z.string().optional(),
  caption: z.string().max(4096).optional(),
  fileName: z.string().max(200).optional(),
  /** audio: enviar como nota de voz */
  ptt: z.boolean().optional(),
  quotedId: QuotedSchema,
});

export const ReactionActionSchema = z.object({
  type: z.literal("reaction"),
  to: JidSchema,
  messageId: z.string().min(1).max(160),
  emoji: z.string().max(8),
  fromMe: z.boolean().optional(),
});

export const EditActionSchema = z.object({
  type: z.literal("edit"),
  to: JidSchema,
  messageId: z.string().min(1).max(160),
  text: z.string().min(1).max(65536),
});

export const DeleteActionSchema = z.object({
  type: z.literal("delete"),
  to: JidSchema,
  messageId: z.string().min(1).max(160),
  fromMe: z.boolean().optional(),
});

export const ReadActionSchema = z.object({
  type: z.literal("read"),
  to: JidSchema,
  messageIds: z.array(z.string().min(1).max(160)).min(1).max(50),
});

export const PresenceActionSchema = z.object({
  type: z.literal("presence"),
  to: JidSchema,
  presence: z.enum(["composing", "recording", "available", "unavailable", "paused"]),
});

export const WhatsappActionSchema = z.discriminatedUnion("type", [
  SendTextActionSchema,
  SendMediaActionSchema,
  ReactionActionSchema,
  EditActionSchema,
  DeleteActionSchema,
  ReadActionSchema,
  PresenceActionSchema,
]);
export type WhatsappAction = z.infer<typeof WhatsappActionSchema>;

export const CheckNumbersSchema = z.object({
  numbers: z.array(z.string().min(5).max(40)).min(1).max(100),
});
export type CheckNumbersInput = z.infer<typeof CheckNumbersSchema>;

export const OnWhatsappEntrySchema = z.object({
  jid: z.string(),
  exists: z.boolean(),
});
export type OnWhatsappEntry = z.infer<typeof OnWhatsappEntrySchema>;

// ── Cola de acciones (outbox) ───────────────────────────────────
export const OutboxStatusSchema = z.enum(["pending", "processing", "done", "failed"]);
export type OutboxStatus = z.infer<typeof OutboxStatusSchema>;

export const QueuedActionSchema = z.object({
  actionId: z.string(),
  status: OutboxStatusSchema,
});
export type QueuedAction = z.infer<typeof QueuedActionSchema>;

export const ActionJobSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  type: z.string(),
  status: OutboxStatusSchema,
  attempts: z.number().int().nonnegative(),
  messageId: z.string().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ActionJob = z.infer<typeof ActionJobSchema>;

// ── Documentación (se sirve solo por endpoint autenticado) ──────
export type DocsBlock =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "steps"; items: string[] }
  | { kind: "code"; language: string; code: string };

export interface DocsSection {
  id: string;
  title: string;
  blocks: DocsBlock[];
}

export interface Docs {
  title: string;
  updatedAt: string;
  intro: string;
  sections: DocsSection[];
}

// ── Mapa de rutas tipadas (fuente de verdad) ────────────────────
// El cliente tipado (src/client/api/client.ts) lo consume directamente.
export const API_ROUTES = {
  health: "/api/health",
  authLogin: "/api/auth/login",
  authMe: "/api/auth/me",
  docs: "/api/docs",
  whatsappSessions: "/api/whatsapp/sessions",
} as const;

// Rutas de una sesión concreta.
export const whatsappSessionRoutes = (id: string) => {
  const base = `/api/whatsapp/sessions/${encodeURIComponent(id)}`;
  return {
    status: base,
    logs: `${base}/logs`,
    connect: `${base}/connect`,
    logout: `${base}/logout`,
    token: `${base}/token`,
    webhook: `${base}/webhook`,
    actions: `${base}/actions`,
    action: (actionId: string) => `${base}/actions/${encodeURIComponent(actionId)}`,
    contactsCheck: `${base}/contacts/check`,
    profilePicture: `${base}/profile-picture`,
  } as const;
};

// Ruta WebSocket (no es HTTP, se resuelve contra el host actual en el front).
export const WHATSAPP_WS_PATH = "/ws/whatsapp";
